import React, { ReactNode, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudTruncText,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import type { OAuthClient } from '../api/types'
import { validateRedirectUri } from '../validation/redirectUri'
import type {
  AdminClientItem,
  AdminCreateClientReq,
  AdminUpdateClientReq,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

const parseList = (v: unknown): string[] =>
  String(v ?? '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)

const columns: CrudColumn<OAuthClient>[] = [
  { key: 'id', header: 'Client ID' },
  { key: 'name', header: 'Name', render: ({ name }) => name ?? '—' },
  { key: 'app_id', header: 'App', render: ({ app_id: appId }) => appId ?? '—' },
  {
    key: 'redirect_uris',
    header: 'Redirect URIs',
    render: ({ redirect_uris: uris }) => {
      const text = uris.join(', ')
      return (
        <CrudTruncText $maxWidth={260} title={text}>
          {text}
        </CrudTruncText>
      )
    },
  },
  {
    key: 'grant_types',
    header: 'Grant Types',
    render: ({ grant_types: types }) => types.join(', '),
  },
]

const clientFields: CrudField[] = [
  { name: 'name', label: 'Name', type: 'text' },
  { name: 'app_id', label: 'App ID (optional)', type: 'text' },
  {
    name: 'redirect_uris',
    label: 'Redirect URIs',
    type: 'urilist',
    required: true,
    validateEntry: validateRedirectUri,
    placeholder: 'https://app.example.com/callback',
  },
  {
    name: 'grant_types',
    label: 'Grant Types',
    type: 'textarea',
    required: true,
  },
  { name: 'scopes', label: 'Scopes', type: 'textarea' },
]

const CREATE_INITIAL: Record<string, unknown> = {}

/**
 * Map the generated AdminClientItem (camelCase) → OAuthClient (snake_case) so
 * the existing column/form/edit logic stays unchanged. The client secret is
 * NEVER present on list/get items — only on create/rotate responses.
 */
function toOAuthClient(c: AdminClientItem): OAuthClient {
  return {
    id: c.id,
    tenant_id: c.tenantId,
    name: c.name,
    app_id: c.appId,
    redirect_uris: c.redirectUris ?? [],
    grant_types: c.grantTypes ?? [],
    scopes: c.scopes ?? [],
    created_at: c.createdAt,
    updated_at: c.updatedAt,
  }
}

const buildEditInitial = (client: OAuthClient): Record<string, unknown> => ({
  name: client.name ?? '',
  app_id: client.app_id ?? '',
  redirect_uris: client.redirect_uris,
  grant_types: client.grant_types.join('\n'),
  scopes: (client.scopes ?? []).join('\n'),
})

const toCreateReq = (
  tenantId: string,
  values: Record<string, unknown>
): AdminCreateClientReq => {
  const scopes = parseList(values.scopes)
  return {
    tenantId,
    name: values.name ? String(values.name) : '',
    appId: values.app_id ? String(values.app_id) : undefined,
    redirectUris: Array.isArray(values.redirect_uris)
      ? (values.redirect_uris as string[])
      : parseList(values.redirect_uris),
    grantTypes: parseList(values.grant_types),
    scopes: scopes.length ? scopes : undefined,
  }
}

const toUpdateReq = (
  tenantId: string,
  clientId: string,
  values: Record<string, unknown>
): AdminUpdateClientReq => {
  const scopes = parseList(values.scopes)
  return {
    tenantId,
    clientId,
    name: values.name ? String(values.name) : undefined,
    appId: values.app_id ? String(values.app_id) : undefined,
    redirectUris: Array.isArray(values.redirect_uris)
      ? (values.redirect_uris as string[])
      : parseList(values.redirect_uris),
    grantTypes: parseList(values.grant_types),
    scopes,
  }
}

// ---------------------------------------------------------------------------
// One-time client-secret callout (Clients-specific — not part of the shared
// crud/ primitives). Preserves the pre-migration MUI Paper's role="status" +
// warning-bordered + monospace-code-block behavior.
// ---------------------------------------------------------------------------

const SecretCallout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  border: 2px solid ${({ theme }) => asUnified(theme).colors?.warning ?? '#92400e'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => `${asUnified(theme).colors?.warning ?? '#92400e'}1a`};
`

const SecretHeading = styled.p`
  margin: 0;
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).colors?.warning ?? '#92400e'};
`

const SecretCode = styled.code`
  display: block;
  font-family: monospace;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.9rem'};
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => asUnified(theme).bg?.surface ?? '#fefcbf'};
  word-break: break-all;
`

export const Clients = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [editingClient, setEditingClient] = useState<OAuthClient | null>(null)
  const [oneTimeSecret, setOneTimeSecret] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<OAuthClient | null>(null)

  const rs = useResource<OAuthClient>({
    list: async () => {
      const resp = await client.adminListClients({ tenantId: tenant })
      return (resp.clients ?? []).map(toOAuthClient)
    },
    remove: async (id) => {
      await client.adminDeleteClient({ tenantId: tenant, clientId: id })
    },
  })

  const handleCreate = async (
    values: Record<string, unknown>
  ): Promise<void> => {
    try {
      const resp = await client.adminCreateClient(toCreateReq(tenant, values))
      setOneTimeSecret(resp.clientSecret ?? null)
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleEdit = async (values: Record<string, unknown>): Promise<void> => {
    if (!editingClient) return
    try {
      await client.adminUpdateClient(
        toUpdateReq(tenant, editingClient.id, values)
      )
      await rs.reload()
      setEditingClient(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleRotateSecret = async (clientId: string): Promise<void> => {
    try {
      const resp = await client.adminRotateClientSecret({
        tenantId: tenant,
        clientId,
      })
      setOneTimeSecret(resp.clientSecret)
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Rotate secret failed')
    }
  }

  const handleCopy = (): void => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(oneTimeSecret ?? '')
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (c: OAuthClient): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditingClient(c)
          setActionError(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudSecondaryButton
        type="button"
        onClick={() => handleRotateSecret(c.id)}
      >
        Rotate Secret
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(c)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>Please select a tenant to manage clients.</TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>OAuth Clients</PageTitle>

      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}

      {oneTimeSecret !== null && (
        <SecretCallout role="status">
          <SecretHeading>
            Client Secret — copy now, it will not be shown again
          </SecretHeading>
          <SecretCode>{oneTimeSecret}</SecretCode>
          <CrudActionsRow>
            <CrudSecondaryButton type="button" onClick={handleCopy}>
              Copy
            </CrudSecondaryButton>
            <CrudSecondaryButton
              type="button"
              onClick={() => setOneTimeSecret(null)}
            >
              Dismiss
            </CrudSecondaryButton>
          </CrudActionsRow>
        </SecretCallout>
      )}

      <SectionBlock>
        <SectionTitle>All Clients</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="OAuth clients"
          emptyMessage="No OAuth clients configured yet."
        />
      </SectionBlock>

      {editingClient && (
        <SectionBlock>
          <SectionTitle>Edit Client</SectionTitle>
          <CrudForm
            key={editingClient.id}
            fields={clientFields}
            initial={buildEditInitial(editingClient)}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditingClient(null)
              setActionError(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionTitle>Create Client</SectionTitle>
        <CrudForm
          fields={clientFields}
          initial={CREATE_INITIAL}
          onSubmit={handleCreate}
          submitLabel="Create Client"
        />
      </SectionBlock>

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete client"
        body={`Delete client "${
          pendingDelete?.name ?? pendingDelete?.id ?? ''
        }"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
